import { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { RecruiterShell } from "@/shell/RecruiterShell";
import { api, ensureCsrf } from "@/lib/api";

const T = {
  ink: "#17131F", paper: "#FBFAFE", accent: "var(--accent)",
  slate: "#7C7590", border: "#E8E4DC", white: "#FFFFFF",
  good: "#1A7A4A", danger: "#B91C1C", dangerBorder: "rgba(185,28,28,0.35)",
  warn: "#92400E", warnBg: "#FFFBEB", warnBorder: "#FDE68A",
};

// Non-cryptographic — only used as a cache-busting src param, see DevicePreview.
function hashCode(s: string): number {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) | 0;
  return h;
}

// Mirrors the logo/filter selection BrandLockup's real callers already use in
// production (HomeShell for the light portal chrome, LiveSession for the dark
// session screen) — so the preview can't show something candidates won't
// actually see. See _brand_data() in service/api.py for the server-side twin.
function previewLogoLight(b: BrandData) {
  const logoUrl = b.logo_url_light || b.logo_url;
  const logoFilter = b.logo_style === "auto_invert" && logoUrl && !b.logo_url_light ? "brightness(0)" : undefined;
  return { logoUrl: logoUrl || undefined, logoFilter };
}

// The backdrop the candidate portal will actually paint, mirroring the
// logo_style branch in _brand_data() (service/api.py). Handed to the preview
// iframe explicitly: CSS custom properties do not cross an iframe boundary,
// so without this the iframe shows the logo on nothing while the cards beside
// it show it on the agency's colour.
function previewBackdrop(b: BrandData): string | undefined {
  if (b.logo_style === "white_bar") return "#F7F4EE";
  // A darkened logo is black, so it goes on the light page, never on the
  // brand colour: black on a dark brand bar was unreadable (QA F19).
  if (b.logo_style === "auto_invert" || b.brand_colour_is_default) return undefined;
  return b.brand_colour || undefined;
}

const cardStyle: React.CSSProperties = {
  background: T.white, borderRadius: 18, padding: 24,
  boxShadow: "0 1px 3px rgba(0,0,0,0.06)", marginBottom: 16,
};

// Renders BrandPreview.tsx in an iframe at its real pixel width (960 = well
// above Tailwind's md: 768px breakpoint, 380 = well below it), then scales
// the iframe down with a CSS transform so it fits the settings card without
// the recruiter having to scroll — the iframe's *internal* viewport is what
// triggers the real breakpoint, the on-page footprint is just cosmetic.
function DevicePreview({
  label, iframeWidth, iframeHeight, scale, previewId, params,
}: {
  label: string;
  iframeWidth: number;
  iframeHeight: number;
  scale: number;
  previewId: string;
  params: Record<string, string | undefined>;
}) {
  // logoUrl can be a base64 data: URI for inline-SVG logos — long enough to
  // blow past gunicorn's 4094-byte request-line limit ("Bad Request /
  // Request Line is too large") if it went into the iframe's query string,
  // so it's handed over via sessionStorage instead. Everything else here is
  // short and stays in the URL.
  const { logoUrl, ...rest } = params;
  const logoKey = `brand-preview-logo:${previewId}`;
  if (logoUrl) sessionStorage.setItem(logoKey, logoUrl);
  else sessionStorage.removeItem(logoKey);
  // The storage key is stable per slot, so it alone can't tell the iframe a
  // *new* logo arrived (e.g. recruiter fetches a second domain without
  // reloading the page) — the src string would be byte-identical and the
  // browser wouldn't renavigate, leaving the preview showing the old logo.
  // `v` is a cheap content hash whose only job is to change the src string.
  const v = logoUrl ? String(Math.abs(hashCode(logoUrl))) : undefined;
  const qs = new URLSearchParams(
    Object.fromEntries(
      Object.entries({ ...rest, logoKey: logoUrl ? logoKey : undefined, v }).filter(([, val]) => val),
    ) as Record<string, string>,
  ).toString();
  return (
    <div style={{ textAlign: "center" }}>
      <div style={{
        fontSize: 10, fontWeight: 600, letterSpacing: "0.08em", textTransform: "uppercase",
        color: T.slate, marginBottom: 6,
      }}>
        {label}
      </div>
      <div style={{
        width: iframeWidth * scale, height: iframeHeight * scale, overflow: "hidden",
        border: `1px solid ${T.border}`, borderRadius: 8,
        background: T.white,
      }}>
        <iframe
          src={`/brand-preview?${qs}`}
          width={iframeWidth}
          height={iframeHeight}
          scrolling="no"
          style={{ border: "none", transform: `scale(${scale})`, transformOrigin: "top left" }}
          title={`${label} brand preview`}
        />
      </div>
    </div>
  );
}

interface BrandData {
  logo_url: string;
  logo_url_light: string;
  logo_style: string;
  brand_colour: string;
  brand_secondary_colour: string;
  subdomain: string;
  website_url: string;
  /** True when brand_colour is the server's house-plum fallback rather than a
   *  colour the agency actually chose. The colour field alone cannot tell
   *  those apart, and the preview must not paint a backdrop nobody picked. */
  brand_colour_is_default?: boolean;
  /** Measured by service/logocheck.py, never guessed from the SVG's markup.
   *  "unverified" means we could not render it, so nothing is claimed and no
   *  card is marked or pre-selected. Transient, never persisted. */
  logo_verdict?: string;
}

// The three real logo styles, each shown as its own rendered preview rather
// than a word in a dropdown: a visual choice cannot be judged from a label.
// Backdrop lives on the wrapper, filter on the <img>, never both on one
// element (see the preview comment above, and service/api.py's twin).
const LOGO_STYLE_CARDS = [
  { value: "variant", label: "On your brand colour",
    hint: "Best for a white or light logo" },
  { value: "auto_invert", label: "Darkened",
    hint: "Best when no brand colour is set" },
  { value: "white_bar", label: "On a cream bar",
    hint: "Best for a dark or full-colour logo" },
] as const;

/** Which cards the measurement says will not read. A white-on-transparent
 *  logo (verdict "dark_only") disappears on cream; a dark logo disappears on
 *  a dark brand colour and gains nothing from being darkened further.
 *  An "unverified" verdict marks nothing: we make no claim we cannot back. */
function unreadableCards(verdict: string | undefined, brandColour: string): string[] {
  if (verdict === "dark_only") {
    // Needs a dark or coloured surface. Cream gives it neither.
    return brandColour ? ["white_bar"] : [];
  }
  if (verdict === "light_only") return ["auto_invert"];
  return [];
}

export function BrandSetup() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isOnboarding = searchParams.get("onboarding") === "1";

  const [brand, setBrand] = useState<BrandData | null>(null);
  const [saving, setSaving] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [message, setMessage] = useState("");
  const [warning, setWarning] = useState("");
  const [error, setError] = useState("");
  const [domain, setDomain] = useState("");
  const [agencyName, setAgencyName] = useState("Executive Interview Coach");

  const formRef = useRef<HTMLFormElement>(null);

  useEffect(() => {
    api.get<BrandData>("/recruiter/brand").then(setBrand).catch(() => {});
    api.get<{ agencyName?: string }>("/me").then((me) => {
      if (me.agencyName) setAgencyName(me.agencyName);
    }).catch(() => {});
  }, []);

  function updateField<K extends keyof BrandData>(k: K, v: BrandData[K]) {
    setBrand((prev) => {
      if (!prev) return prev;
      const next = { ...prev, [k]: v };
      // Editing the colour makes it a real choice, so the server's
      // "this is only the house-plum fallback" flag stops applying. Without
      // this the preview keeps suppressing the backdrop for a colour the
      // recruiter just picked.
      if (k === "brand_colour") next.brand_colour_is_default = !String(v || "").trim();
      return next;
    });
  }

  async function handleFetch(e: React.FormEvent) {
    e.preventDefault();
    setFetching(true); setError(""); setMessage(""); setWarning("");
    try {
      const res = await api.post<BrandData & { ok: boolean; error?: string; message?: string; logo_is_generic_icon?: boolean }>(
        "/recruiter/brand/fetch", { domain }
      );
      if (res.ok) {
        // The API always returns these three fields after resolving what to
        // keep vs overwrite server-side (an empty logo_url is a real "we
        // found nothing, don't show a wrong guess" result, not a missing
        // value) — use them directly rather than `res.x || prev.x`, which
        // silently kept the *previous* domain's logo on screen whenever a
        // fresh fetch correctly came back empty.
        if (res.logo_is_generic_icon) {
          // A generic favicon/touch-icon was the only signal on the page
          // (common on JS-rendered sites, e.g. Wix, where the real logo
          // never appears in the server HTML) — technically "found a logo",
          // but not one worth presenting as a confident result.
          setWarning("Only found a generic site icon, not a dedicated logo — for best results, upload the agency's logo directly below.");
        } else {
          setMessage(
            res.logo_url
              ? (res.message ?? "Brand fetched.")
              : "Colours updated — no clear logo found on that domain. Set one manually below.",
          );
        }
        setBrand((prev) => prev ? { ...prev,
          // A generic icon isn't worth presenting as the agency's logo (see
          // the warning above) — don't save it as one, keep whatever was
          // there before and let the recruiter upload the real thing.
          logo_url: res.logo_is_generic_icon ? prev.logo_url : res.logo_url,
          brand_colour: res.brand_colour || prev.brand_colour,
          brand_secondary_colour: res.brand_secondary_colour || prev.brand_secondary_colour,
          logo_verdict: res.logo_verdict || "unverified",
          // A colour that just came back from a fetch is a real one.
          brand_colour_is_default: res.brand_colour ? false : prev.brand_colour_is_default,
          // Pre-select from what was measured, not from the fill-string guess.
          // "dark_only" is a white-on-transparent logo: it needs a coloured
          // surface, so prefer the brand colour when the fetch found one, and
          // fall back to darkening it when it did not. "unverified" changes
          // nothing, because nothing was established.
          // Only res.brand_colour counts. prev.brand_colour is always truthy:
          // the brand API falls back to house plum (#7A6BE0) for an agency
          // that never set one, so testing it would pick "variant" and preview
          // the logo on our plum instead of theirs.
          logo_style:
            res.logo_verdict === "dark_only"
              ? (res.brand_colour ? "variant" : "auto_invert")
              : res.logo_verdict === "light_only"
                ? "white_bar"
                // Measured readable on dark too, so show it in its own
                // colours on the brand colour rather than darkening it.
                : res.logo_verdict === "both" && res.brand_colour
                  ? "variant"
                  : (res.logo_style || prev.logo_style),
        } : prev);
      } else {
        setError(res.error ?? "No brand data found.");
      }
    } catch {
      setError("Brand fetch failed. Set manually below.");
    }
    setFetching(false);
  }

  async function handleSave(e: React.FormEvent) {
    e.preventDefault();
    if (!formRef.current || !brand) return;
    setSaving(true); setError(""); setMessage(""); setWarning("");
    const formData = new FormData(formRef.current);
    // Inject text fields from state (in case file inputs aren't in formData)
    formData.set("brand_colour", brand.brand_colour);
    formData.set("brand_secondary_colour", brand.brand_secondary_colour);
    formData.set("subdomain", brand.subdomain);
    formData.set("logo_url", brand.logo_url);
    formData.set("logo_url_light", brand.logo_url_light);
    formData.set("logo_style", brand.logo_style);
    if (isOnboarding) formData.set("onboarding", "1");
    try {
      const csrfToken = await ensureCsrf();
      const res = await fetch("/api/v1/recruiter/brand", {
        method: "PATCH",
        headers: { "X-CSRFToken": csrfToken },
        body: formData,
        credentials: "include",
      });
      const json = await res.json() as { ok: boolean; error?: string; message?: string };
      if (json.ok) {
        if (isOnboarding) {
          navigate("/coach/recruiter/invite/new");
          return;
        }
        setMessage(json.message ?? "Branding saved.");
        window.dispatchEvent(new Event("branding:updated"));
      } else {
        setError(json.error ?? "Save failed.");
      }
    } catch {
      setError("Save failed. Please try again.");
    }
    setSaving(false);
  }

  async function handleClearLogo() {
    try {
      await api.delete("/recruiter/brand/logo");
      setBrand((prev) => prev ? { ...prev, logo_url: "", logo_url_light: "" } : prev);
      setMessage("Logo cleared.");
      window.dispatchEvent(new Event("branding:updated"));
    } catch {
      setError("Could not clear logo.");
    }
  }

  if (!brand) {
    return (
      <RecruiterShell activeNav="settings">
        <div style={{ padding: 32, color: T.slate, fontSize: 14 }}>Loading…</div>
      </RecruiterShell>
    );
  }

  const inner = (
    <div style={{ maxWidth: 600, margin: "0 auto" }}>
      <div style={{ marginBottom: 28 }}>
        <h1 style={{ margin: 0, fontSize: 24, fontWeight: 700, color: T.ink }}>
          Brand setup
        </h1>
        <p style={{ margin: "6px 0 0", fontSize: 13.5, color: T.slate }}>
          Customise how candidates see your agency in their interview portal.
        </p>
      </div>

      {message && (
        <div style={{ marginBottom: 16, padding: "12px 14px", background: "#F0FDF4",
          borderRadius: 8, border: "1px solid #BBF7D0", color: T.good, fontSize: 13.5 }}>
          {message}
        </div>
      )}
      {warning && (
        <div style={{ marginBottom: 16, padding: "12px 14px", background: T.warnBg,
          borderRadius: 8, border: `1px solid ${T.warnBorder}`, color: T.warn, fontSize: 13.5 }}>
          {warning}
        </div>
      )}
      {error && (
        <div style={{ marginBottom: 16, padding: "12px 14px", background: "#FEF2F2",
          borderRadius: 8, border: "1px solid #FECACA", color: T.danger, fontSize: 13.5 }}>
          {error}
        </div>
      )}

      {/* Auto-fetch section */}
      <div style={cardStyle}>
        <p style={{ margin: "0 0 12px", fontSize: 13, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: T.slate }}>
          Auto-fetch from domain
        </p>
        <form onSubmit={handleFetch} style={{ display: "flex", gap: 10, alignItems: "flex-end" }}>
          <div style={{ flex: 1 }}>
            <label style={{ display: "block", fontSize: 12.5, color: T.slate, marginBottom: 4 }}>
              Agency domain
            </label>
            <input
              type="text" value={domain} onChange={(e) => setDomain(e.target.value)}
              placeholder="acme.com" required
              style={{ display: "block", width: "100%", padding: "9px 12px", fontSize: 14,
                border: `1px solid ${T.border}`, borderRadius: 8, outline: "none",
                color: T.ink, background: T.paper, boxSizing: "border-box" }}
            />
          </div>
          <button type="submit" disabled={fetching}
            style={{ padding: "9px 18px", fontSize: 13.5, fontWeight: 600, color: T.white,
              background: fetching ? T.slate : T.accent, border: "none", borderRadius: 8,
              cursor: fetching ? "default" : "pointer", flexShrink: 0, whiteSpace: "nowrap" }}>
            {fetching ? "Fetching…" : "Fetch brand"}
          </button>
        </form>
      </div>

      {/* Brand settings form */}
      <form ref={formRef} onSubmit={handleSave} encType="multipart/form-data">
        {/* Logo */}
        <div style={cardStyle}>
          <p style={{ margin: "0 0 16px", fontSize: 13, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: T.slate }}>
            Logo
          </p>
          <div style={{ marginBottom: 12, display: "flex", alignItems: "center", gap: 12 }}>
            {/* The background and border live on this wrapper, never on the
                <img> that carries the filter. A CSS filter paints everything
                the element draws, its own background included, so
                background:white + brightness(0) on one element crushed the
                whole card to a solid black block and hid the (correctly
                blackened) logo inside it. The fixed width and height are load
                bearing too: a scraped SVG often declares only a viewBox, and
                with no intrinsic size it collapses to 0x0 in a shrink-to-fit
                parent. */}
            {brand.logo_url && (
              <div style={{ display: "flex", width: 176, height: 48, alignItems: "center", justifyContent: "center", border: `1px solid ${T.border}`, borderRadius: 6, background: previewBackdrop(brand) || T.white, flexShrink: 0 }}>
                {/* Backdrop follows the selected style, or this box renders a
                    white-on-transparent logo onto white and shows nothing at
                    all: the same "empty box" the recruiter reported, moved
                    rather than fixed. */}
                <img src={brand.logo_url} alt="Logo" style={{ maxHeight: 40, maxWidth: 160, objectFit: "contain", filter: previewLogoLight(brand).logoFilter }} />
              </div>
            )}
            {brand.logo_url && (
              <button type="button" onClick={handleClearLogo}
                style={{ padding: "9px 18px", fontSize: 13.5, fontWeight: 600, color: T.danger,
                  background: "transparent", border: `1px solid ${T.dangerBorder}`, borderRadius: 8,
                  cursor: "pointer", flexShrink: 0, whiteSpace: "nowrap" }}>
                Remove
              </button>
            )}
          </div>
          {/* type="text", NOT type="url". This field legitimately holds a
              relative path after a file upload ("/agency/logo/<id>_main.png")
              and a data:image/svg+xml URI after a domain scrape, and the browser
              rejects both as a typeMismatch on type="url" — which blocked form
              submission entirely, with no error banner and no network request.
              Confirmed in a real browser: validationMessage "Please enter a URL",
              form.checkValidity() false. So once a recruiter uploaded a logo,
              every later save on this page silently did nothing. The server
              validates the scheme properly (see api_recruiter_brand_save). */}
          <FieldGroup label="Logo URL (or upload below)">
            <input type="text" name="logo_url" value={brand.logo_url}
              onChange={(e) => updateField("logo_url", e.target.value)}
              placeholder="https://..." style={inputStyle} />
          </FieldGroup>
          <FieldGroup label="Upload logo file">
            <input type="file" name="logo_file" accept="image/png,image/jpeg,image/svg+xml,image/webp"
              style={{ fontSize: 13.5, color: T.ink }} />
          </FieldGroup>
          <FieldGroup label="Light logo (for dark backgrounds, optional)">
            <input type="file" name="logo_file_light" accept="image/png,image/jpeg,image/svg+xml,image/webp"
              style={{ fontSize: 13.5, color: T.ink }} />
          </FieldGroup>
          <FieldGroup label="Logo display style">
            {/* A hidden input keeps logo_style in the form payload now that
                the <select> that used to carry it is gone. */}
            <input type="hidden" name="logo_style" value={brand.logo_style} />
            <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
              {LOGO_STYLE_CARDS.map((c) => {
                const selected = brand.logo_style === c.value;
                const dim = unreadableCards(brand.logo_verdict, brand.brand_colour).includes(c.value);
                const backdrop =
                  c.value === "variant" ? (brand.brand_colour || T.white)
                  : c.value === "white_bar" ? "#F7F4EE"
                  : T.white;
                return (
                  <button key={c.value} type="button" aria-pressed={selected}
                    onClick={() => updateField("logo_style", c.value)}
                    style={{
                      display: "flex", flexDirection: "column", gap: 8, padding: 10,
                      border: `2px solid ${selected ? T.accent : T.border}`,
                      borderRadius: 10, background: T.white, cursor: "pointer",
                      opacity: dim ? 0.45 : 1, textAlign: "left",
                    }}>
                    {/* Backdrop here, filter on the image below. Never both on
                        one element: a CSS filter paints its own background. */}
                    <span style={{
                      display: "flex", width: 168, height: 46, alignItems: "center",
                      justifyContent: "center", borderRadius: 6, background: backdrop,
                    }}>
                      {brand.logo_url
                        ? <img src={brand.logo_url} alt=""
                            style={{ maxHeight: 38, maxWidth: 150, objectFit: "contain",
                                     filter: c.value === "auto_invert" ? "brightness(0)" : undefined }} />
                        : <span style={{ fontSize: 11, color: T.slate }}>{agencyName}</span>}
                    </span>
                    <span style={{ fontSize: 12.5, fontWeight: 600, color: T.ink }}>{c.label}</span>
                    <span style={{ fontSize: 11, color: dim ? T.danger : T.slate }}>
                      {dim ? "Hard to read with this logo" : c.hint}
                    </span>
                  </button>
                );
              })}
            </div>
            {brand.logo_verdict === "unverified" && brand.logo_url && (
              <p style={{ margin: "10px 0 0", fontSize: 12, color: T.slate }}>
                We could not render this logo to check it, so nothing is marked
                below. Pick whichever option looks right to you.
              </p>
            )}
          </FieldGroup>
        </div>

        {/* Live preview — real BrandLockup rendered at real desktop + mobile
            viewports via iframe. A <div> narrowed with CSS can't trigger
            BrandLockup's md: breakpoint (media queries key off the actual
            browser viewport, not a container's width) — the iframe is the
            only way to show what a candidate's phone will really render,
            not a scaled-down desktop layout. */}
        <div style={{ ...cardStyle, padding: 0, overflow: "hidden" }}>
          <div style={{ padding: 20, background: T.paper }}>
            <p style={{ margin: "0 0 4px", fontSize: 13, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: T.slate }}>
              Preview
            </p>
            {/* The preview renders unsaved form state. It used to say nothing
                about that, so when a save silently discarded the logo the
                preview kept showing it and looked simply wrong. The save is
                honest now, and so is this. */}
            <p style={{ margin: "0 0 16px", fontSize: 12.5, color: T.slate }}>
              How candidates will see your agency once you save. Changes above are not live until you press Save branding.
            </p>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 16, justifyContent: "center" }}>
              <DevicePreview label="Desktop" iframeWidth={960} iframeHeight={140} scale={0.57} previewId="portal-desktop"
                params={{ ...previewLogoLight(brand), agencyName: agencyName || "", inline: "1", logoBackdrop: previewBackdrop(brand) }} />
              {/* Matches HomeShell's real mobile branch — non-inline,
                  align="left" — not inline="1", which is what desktop uses.
                  180 (not 140, like desktop) — the fixed STAGING banner +
                  its layout spacer take 34px, and the stacked two-line
                  mobile lockup is taller than desktop's single row, so 140
                  wasn't enough room; the centred content was overflowing
                  the visible iframe and hugging its bottom edge (measured
                  via document.body.scrollHeight = 174 against a 140px
                  iframe). */}
              <DevicePreview label="Mobile" iframeWidth={380} iframeHeight={180} scale={0.85} previewId="portal-mobile"
                params={{ ...previewLogoLight(brand), agencyName: agencyName || "", align: "left", logoBackdrop: previewBackdrop(brand) }} />
            </div>
            <div style={{ display: "flex", justifyContent: "center", marginTop: 14 }}>
              <div style={{ background: brand.brand_colour || T.accent, color: "#fff", padding: "10px 20px",
                borderRadius: 8, fontSize: 12.5, fontWeight: 600 }}>Get started &#8594;</div>
            </div>
          </div>
        </div>

        {/* Colours */}
        <div style={cardStyle}>
          <p style={{ margin: "0 0 16px", fontSize: 13, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: T.slate }}>
            Brand colours
          </p>
          <FieldGroup label="Primary colour">
            <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
              <input type="color" name="brand_colour" value={brand.brand_colour}
                onChange={(e) => updateField("brand_colour", e.target.value)}
                style={{ width: 48, height: 36, padding: 2, borderRadius: 6, border: `1px solid ${T.border}`, cursor: "pointer" }} />
              <input type="text" value={brand.brand_colour}
                onChange={(e) => updateField("brand_colour", e.target.value)}
                placeholder="#7A6BE0"
                style={{ ...inputStyle, width: 100, flex: "none" }} />
              <span style={{ fontSize: 13, color: T.slate }}>Used for buttons and accents on candidate pages</span>
            </div>
          </FieldGroup>
          <FieldGroup label="Secondary colour (optional)">
            <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
              <input type="color" name="brand_secondary_colour"
                value={brand.brand_secondary_colour || "#000000"}
                onChange={(e) => updateField("brand_secondary_colour", e.target.value)}
                style={{ width: 48, height: 36, padding: 2, borderRadius: 6, border: `1px solid ${T.border}`, cursor: "pointer" }} />
              <input type="text" value={brand.brand_secondary_colour}
                onChange={(e) => updateField("brand_secondary_colour", e.target.value)}
                placeholder="#000000 or leave blank"
                style={{ ...inputStyle, width: 160, flex: "none" }} />
            </div>
          </FieldGroup>
        </div>

        <button type="submit" disabled={saving}
          style={{ display: "block", width: "100%", padding: "14px 0", fontSize: 15, fontWeight: 600,
            color: T.white, background: saving ? T.slate : T.accent, border: "none",
            borderRadius: 8, cursor: saving ? "default" : "pointer" }}>
          {saving ? "Saving…" : isOnboarding ? "Save and send first invite →" : "Save branding"}
        </button>

        {isOnboarding && (
          <button type="button" onClick={() => navigate("/coach/recruiter")}
            style={{ display: "block", width: "100%", marginTop: 10, padding: "12px 0", fontSize: 14,
              color: T.slate, background: "transparent", border: "none", cursor: "pointer" }}>
            Skip for now, go to dashboard
          </button>
        )}
      </form>
    </div>
  );

  return <RecruiterShell activeNav="settings">{inner}</RecruiterShell>;
}

function FieldGroup({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <label style={{ display: "block", fontSize: 12.5, fontWeight: 600, color: "#7C7590", marginBottom: 5 }}>
        {label}
      </label>
      {children}
    </div>
  );
}

const inputStyle: React.CSSProperties = {
  display: "block", width: "100%", padding: "9px 12px", fontSize: 14,
  border: "1px solid #E8E4DC", borderRadius: 8, outline: "none",
  color: "#17131F", background: "#FBFAFE", boxSizing: "border-box",
};
